import { FieldValues, Path, useController } from "react-hook-form";
import { Checkbox, FormControl, FormErrorMessage, CheckboxProps } from "@chakra-ui/react";
import { Box } from "@familyzone/component-library";
import { ConfigFormLabel } from "./ConfigFormLabel";
import React from "react";
import { FieldProps } from "./Common";

type Props<TFieldValues extends FieldValues, TFieldPath extends Path<TFieldValues> = Path<TFieldValues>> = {
  fieldProps: FieldProps<TFieldValues, TFieldPath>;
  inputProps?: CheckboxProps;
};

export const CheckBoxControl = <TFieldValues extends FieldValues, TFieldPath extends Path<TFieldValues>>({
  fieldProps,
  inputProps,
}: Props<TFieldValues, TFieldPath>): JSX.Element => {
  const { name, control, errors, rules, label, tooltip, testId, onBlur, onChange, disabled } = fieldProps;
  const { field } = useController({
    name,
    control,
    rules: rules,
  });

  return (
    <FormControl
      isInvalid={!!errors[name]}
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "20px",
        fontSize: "14px",
        alignItems: "start",
      }}
    >
      <Box
        sx={{
          display: "flex",
        }}
      >
        <Checkbox
          // For whatever reason, `<Checkbox>` requires using `inputProps` to add props to the input.
          // If you don't use `inputProps`, extra props end up on the checkbox label for some reason.
          inputProps={
            {
              "data-testid": !!testId ? testId : `${name}-input`,
            } as React.InputHTMLAttributes<HTMLInputElement>
          }
          sx={{
            padding: "0px 12px",
            alignContent: "center",
          }}
          // React hook form.
          disabled={disabled}
          isChecked={field.value}
          onChange={(e) => {
            field.onChange(e);
            if (onChange !== undefined) {
              onChange(e);
            }
          }}
          onBlur={(e) => {
            field.onBlur();
            if (onBlur !== undefined) {
              onBlur(e);
            }
          }}
          name={field.name}
          ref={field.ref}
          // Forward the checkbox props.
          {...inputProps}
        />
        <Box
          sx={{
            display: "flex",
          }}
        >
          <ConfigFormLabel label={label} tooltip={tooltip} />
        </Box>
      </Box>
      <FormErrorMessage>{errors[name] && errors[name]?.message}</FormErrorMessage>
    </FormControl>
  );
};
